import { computed, type Ref, ref } from 'vue';

import { ROLE, useClientStore } from '@/features/Clients';
import type { ContractResponse } from '@/features/Contract/types';
import { CONTRACT_TYPE } from '@/features/Contract/types';
import {
  buildContractRequestData,
  buildInventoryContract,
} from '@/features/Contract/useContract/helpers';
import type { UseContract } from '@/features/Contract/useContract/types';
import { DISTRIBUTION_WAY } from '@/features/Distribution/types';
import { useQuoteStore } from '@/features/Quotes/store';
import { useTariffStore } from '@/features/Tariffs/store';
import BackendValidationError from '@/features/validation/backendValidation';
import { useApi } from '@/services/api/useApi';
import { VGL_ERROR_CODE } from '@/services/errorHandler/errorCodes';
import { getErrorMessage } from '@/services/errorHandler/utils';
import { VglError } from '@/services/errorHandler/vglError';

import { useDistributionStore } from '@/features/Distribution/store';
import { ValidationError } from 'yup';
import type { UseLogger } from '@/services/logger/useLogger';
import { useForm } from '@/utils/form/useForm';
import { JSendError } from '@/utils/jSend/jSendError';
import { useDistributionSelection } from '@/features/Distribution/useDistributionSelection';

const isLoading = ref(false);
const overrideVNClientData = ref(true);
const highlightChangedClientFields: Ref<string[]> = ref([]);

const contractRequestResponse = ref<ContractResponse>();
const contractRequestError = ref<unknown>();

const contractType = computed(() => {
  const tariffStore = useTariffStore();
  const { brokerIdForSelectedDistribution, selectedDistributionWay } =
    useDistributionSelection(tariffStore.contractTariff.gesellschaft.id);

  /**
   * We allow initial contracts for FF pooling
   */
  const tariff = useTariffStore().contractTariff;
  const { selectedPoolId } = useDistributionSelection(tariff.gesellschaft.id);

  if (selectedPoolId.value === 'fondsfinanz') {
    const poolInformation = useDistributionStore().getSelectedPool(
      tariff.gesellschaft.id,
      selectedPoolId.value,
    );

    if (
      poolInformation !== undefined &&
      poolInformation.vermittlernummern.length === 0
    ) {
      return CONTRACT_TYPE.INITIAL;
    }
  }

  /**
   * USE_RELATED_COMPANY is only relevant when the user has
   * no brokerId for any axa company while trying to create a dbv
   * contract.
   * This is only a HACK for the DBV as they don't accept initial contracts
   */
  if (
    brokerIdForSelectedDistribution.value === null &&
    (selectedDistributionWay.value === DISTRIBUTION_WAY.USE_COMPANY ||
      selectedDistributionWay.value === DISTRIBUTION_WAY.USE_RELATED_COMPANY)
  ) {
    return CONTRACT_TYPE.INITIAL;
  }

  return CONTRACT_TYPE.ONLINE;
});

const resetErrorState = (): void => {
  contractRequestError.value = undefined;
};

const generateContract = async (): Promise<void> => {
  isLoading.value = true;
  resetErrorState();

  try {
    if (overrideVNClientData.value) {
      await useClientStore().saveClientIfDirty(ROLE.VERSICHERUNGSNEHMER);
    }

    const api = useApi().contract;

    const requestData = buildContractRequestData();

    const [, request] = api.generateContract(requestData, contractType.value);

    const result = await request;
    if (result === null) {
      throw new VglError(VGL_ERROR_CODE.GENERAL_UNEXPECTED_EMPTY_RESPONSE, {
        additionalErrorMessage: 'useContract -> generateContract',
      });
    }

    contractRequestResponse.value = result;

    const inventoryContract = buildInventoryContract(requestData);
    await useQuoteStore().generateInventoryContract(
      inventoryContract,
      useTariffStore().calculationId,
      result.contractId,
    );
  } catch (error) {
    if (error instanceof BackendValidationError) {
      throw error;
    }

    if (error instanceof JSendError) {
      contractRequestError.value = error.data;

      throw error;
    }

    throw new VglError(VGL_ERROR_CODE.CONTRACT_GENERATE_FAILED, {
      additionalErrorMessage: `Error: ${getErrorMessage(error)}`,
      cause: error,
    });
  } finally {
    isLoading.value = false;
  }
};

const handleError = (
  error: unknown,
  logger: UseLogger,
  errors: Ref<Record<string, string[]>>,
): void => {
  const isNoValidationError =
    !(error instanceof ValidationError) &&
    !(error instanceof BackendValidationError);

  if (isNoValidationError) {
    logger.error('contract creation failed', error);
  } else {
    logger.info('contract validation error', errors.value);

    if (error instanceof BackendValidationError) {
      useForm().scrollToFirstError(errors.value);
    }
  }

  if (error instanceof VglError) {
    throw error;
  }

  if (isNoValidationError) {
    // errors concerning the contract generation should be handled already
    throw new VglError(VGL_ERROR_CODE.GENERAL_GENERIC, {
      additionalErrorMessage: `Error: ${getErrorMessage(error)}`,
      cause: error,
    });
  }
};

export const useContract = (): UseContract => ({
  isLoading,
  overrideVNClientData,
  highlightChangedClientFields,

  contractType,
  contractRequestResponse,
  contractRequestError,

  generateContract,
  resetErrorState,
  handleError,
});
